import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Tab,
  Tabs,
  Select,
  MenuItem,
  FormControl,
  AppBar,
  Toolbar
} from '@mui/material';
import { Editor } from '@monaco-editor/react';
import axios from 'axios';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FloatingWaveform from '../components/MockInterview/FloatingWaveform';
import OutputPanel from '../components/MockInterview/OutputPanel'; // NEW IMPORT

const MockInterviewSession = () => {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const [question, setQuestion] = useState(null);
  const [messages, setMessages] = useState([]);
  const [code, setCode] = useState('');
  const [language, setLanguage] = useState('python');
  const [isLoading, setIsLoading] = useState(false);
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [submissionResults, setSubmissionResults] = useState(null);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [outputTab, setOutputTab] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const timerRef = useRef(null);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);

  useEffect(() => {
    fetchQuestion();
    setupSpeechRecognition();

    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (recognition) recognition.stop();
    };
  }, [questionId]);

  useEffect(() => {
    if (question) {
      initializeInterview();
    }
  }, [question]);

  useEffect(() => {
    if (!isAISpeaking && !isLoading && recognition) {
      startListening();
    } else if ((isAISpeaking || isLoading) && recognition) {
      stopListening();
    }
  }, [isAISpeaking, isLoading]);

  const setupSpeechRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.error('Speech Recognition not supported');
      return;
    }

    const recognitionInstance = new SpeechRecognition();
    recognitionInstance.continuous = true;
    recognitionInstance.interimResults = true;
    recognitionInstance.lang = 'en-US';

    recognitionInstance.onstart = () => {
      console.log('🎤 Recognition started');
      setIsListening(true);
    };

    recognitionInstance.onaudiostart = () => {
      console.log('🎤 Audio detected - USER SPEAKING');
      setIsUserSpeaking(true);
    };

    recognitionInstance.onaudioend = () => {
      console.log('🎤 Audio ended - USER STOPPED');
      setIsUserSpeaking(false);
    };

    recognitionInstance.onspeechstart = () => {
      console.log('🗣️ Speech detected');
      setIsUserSpeaking(true);
    };

    recognitionInstance.onspeechend = () => {
      console.log('🗣️ Speech ended');
      setIsUserSpeaking(false);
    };

    recognitionInstance.onresult = (event) => {
      const lastResult = event.results[event.results.length - 1];

      if (lastResult.isFinal) {
        const transcript = lastResult[0].transcript;
        console.log('✅ Final transcript:', transcript);
        setIsUserSpeaking(false);
        handleTranscript(transcript);
      } else {
        console.log('⏳ Interim result...');
        setIsUserSpeaking(true);
      }
    };

    recognitionInstance.onerror = (event) => {
      console.error('❌ Speech recognition error:', event.error);
      setIsUserSpeaking(false);

      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        setTimeout(() => {
          if (!isAISpeaking && !isLoading) {
            startListening();
          }
        }, 1000);
      }
    };

    recognitionInstance.onend = () => {
      console.log('🛑 Recognition ended');
      setIsListening(false);
      setIsUserSpeaking(false);

      if (!isAISpeaking && !isLoading) {
        setTimeout(() => startListening(), 500);
      }
    };

    setRecognition(recognitionInstance);
  };

  const startListening = () => {
    if (recognition && !isListening) {
      try {
        recognition.start();
      } catch (error) {
        console.error('Error starting recognition:', error);
      }
    }
  };

  const stopListening = () => {
    if (recognition && isListening) {
      try {
        recognition.stop();
      } catch (error) {
        console.error('Error stopping recognition:', error);
      }
    }
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const fetchQuestion = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACK_END_URL}/mock-interview/questions/${questionId}`
      );
      setQuestion(response.data);
      setCode(response.data.starter_code[language] || '');
    } catch (error) {
      console.error('Error fetching question:', error);
    }
  };

  const initializeInterview = async () => {
    const welcomeMessage = {
      role: 'assistant',
      content: `Hello! I'll be your interviewer today. We'll be working on "${question.title}" - a ${question.difficulty} level problem. Take your time to read through the problem. When you're ready, you can start coding or discuss your approach with me.`
    };

    setMessages([welcomeMessage]);

    try {
      const audioResponse = await axios.post(
        `${process.env.REACT_APP_BACK_END_URL}/mock-interview/text-to-speech`,
        { text: welcomeMessage.content }
      );

      setIsAISpeaking(true);
      setCurrentAudio(audioResponse.data.audio);
      setTimeout(() => setIsAISpeaking(false), estimateAudioDuration(welcomeMessage.content));
    } catch (error) {
      console.error('Error generating welcome audio:', error);
    }
  };

  const estimateAudioDuration = (text) => {
    const words = text.split(' ').length;
    return (words / 2.5) * 1000;
  };

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = { role: 'user', content: messageText };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACK_END_URL}/mock-interview/chat`,
        {
          messages: updatedMessages,
          question_id: questionId
        }
      );

      const aiMessage = {
        role: 'assistant',
        content: response.data.message
      };

      setMessages([...updatedMessages, aiMessage]);
      setIsAISpeaking(true);

      if (response.data.audio) {
        setCurrentAudio(response.data.audio);
      }

      setTimeout(() => setIsAISpeaking(false), estimateAudioDuration(aiMessage.content));
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTranscript = async (transcript) => {
    if (!transcript.trim()) return;
    console.log('Transcript received:', transcript);
    await sendMessage(transcript);
  };

  const runCode = async () => {
    if (!code.trim() || isLoading) return;

    setIsLoading(true);
    setOutputTab(0);
    setSubmissionResults(null); // Clear previous results

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACK_END_URL}/mock-interview/run-code`,
        {
          question_id: questionId,
          code: code,
          language: language
        }
      );

      setSubmissionResults(response.data);
    } catch (error) {
      console.error('Error running code:', error);
      setSubmissionResults({
        status: 'error',
        error_type: 'Network Error',
        error_message: error.response?.data?.detail || 'Failed to execute code. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const runTests = async () => {
    if (!code.trim() || isLoading) return;

    setIsLoading(true);
    setOutputTab(1);
    setSubmissionResults(null); // Clear previous results

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACK_END_URL}/mock-interview/run-tests`,
        {
          question_id: questionId,
          code: code,
          language: language
        }
      );

      setSubmissionResults(response.data);
    } catch (error) {
      console.error('Error running tests:', error);
      setSubmissionResults({
        status: 'error',
        error_type: 'Network Error',
        error_message: error.response?.data?.detail || 'Failed to run tests. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const submitCode = async () => {
    if (!code.trim() || isLoading) return;

    if (!window.confirm('Are you sure you want to submit? This will end your interview.')) {
      return;
    }

    setIsLoading(true);
    setOutputTab(1);
    setSubmissionResults(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACK_END_URL}/mock-interview/submit-code`,
        {
          question_id: questionId,
          code: code,
          language: language
        }
      );

      setSubmissionResults(response.data);

      setTimeout(() => {
        if (response.data.all_passed) {
          alert(`Congratulations! You passed all ${response.data.total_tests} test cases!`);
        } else {
          alert(`Submission complete. Passed ${response.data.passed}/${response.data.total_tests} test cases.`);
        }
      }, 500);

    } catch (error) {
      console.error('Error submitting code:', error);
      setSubmissionResults({
        status: 'error',
        error_type: 'Network Error',
        error_message: error.response?.data?.detail || 'Failed to submit code. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const submitInterview = async () => {
    await submitCode();

    setTimeout(() => {
      if (window.confirm('Interview completed! Return to question selection?')) {
        navigate('/mock-interview');
      }
    }, 2000);
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'Easy': 'success',
      'Medium': 'warning',
      'Hard': 'error'
    };
    return colors[difficulty] || 'default';
  };

  if (!question) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#ffffff' }}>
      {/* Main Content Area */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        {/* Left Panel - Question Description */}
        <Box sx={{
          width: '40%',
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper'
        }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Question" />
            <Tab label="Hints" />
            <Tab label="Solution" />
            <Tab label="Transcript" />
          </Tabs>

          <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
            {activeTab === 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {question.title}
                  </Typography>
                  <Chip
                    label={question.difficulty}
                    color={getDifficultyColor(question.difficulty)}
                    size="small"
                  />
                </Box>

                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 3 }}>
                  {question.description}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" sx={{ mb: 2 }}>
                  Examples:
                </Typography>

                {question.test_cases.map((tc, idx) => (
                  <Paper
                    key={idx}
                    variant="outlined"
                    sx={{ mb: 2, p: 2 }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Example {idx + 1}:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      <strong>Input:</strong> {tc.input}
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      <strong>Output:</strong> {JSON.stringify(tc.expected_output)}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Hints
                </Typography>
                <Typography color="text.secondary">
                  Ask the AI interviewer for hints by speaking. The interviewer will guide you with probing questions rather than direct answers.
                </Typography>
              </Box>
            )}

            {activeTab === 2 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Solution
                </Typography>
                <Typography color="text.secondary">
                  Solutions are not provided during the interview. Focus on explaining your approach and reasoning to the interviewer.
                </Typography>
              </Box>
            )}

            {activeTab === 3 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Interview Transcript
                </Typography>
                {messages.map((message, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 'bold',
                        color: message.role === 'user' ? 'primary.main' : 'success.main'
                      }}
                    >
                      {message.role === 'user' ? 'You:' : 'Interviewer:'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        pl: 2,
                        py: 0.5,
                        whiteSpace: 'pre-wrap'
                      }}
                    >
                      {message.content}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* Right Panel - Code Editor & Output */}
        <Box sx={{ width: '60%', display: 'flex', flexDirection: 'column' }}>
          {/* Language Selector */}
          <Box sx={{
            p: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'background.paper'
          }}>
            <FormControl size="small">
              <Select
                value={language}
                onChange={(e) => {
                  setLanguage(e.target.value);
                  setCode(question.starter_code[e.target.value] || '');
                }}
                sx={{ minWidth: 120 }}
              >
                <MenuItem value="python">Python</MenuItem>
                <MenuItem value="java">Java</MenuItem>
              </Select>
            </FormControl>
            <Button
              size="small"
              onClick={() => setCode(question.starter_code[language] || '')}
            >
              Reset
            </Button>
          </Box>

          {/* Code Editor */}
          <Box sx={{ flexGrow: 1, height: '50%' }}>
            <Editor
              height="100%"
              language={language}
              value={code}
              onChange={(value) => setCode(value || '')}
              theme="vs-dark"
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                automaticLayout: true,
                tabSize: 4
              }}
            />
          </Box>

          {/* Output Panel - UPDATED */}
          <Box sx={{ height: '50%', borderTop: '1px solid', borderColor: 'divider' }}>
            <OutputPanel
              outputTab={outputTab}
              setOutputTab={setOutputTab}
              submissionResults={submissionResults}
              isLoading={isLoading}
            />
          </Box>
        </Box>
      </Box>

      {/* Bottom Bar */}
      <AppBar
        position="static"
        elevation={0}
        color="default"
        sx={{ borderTop: '1px solid', borderColor: 'divider' }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: '56px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon />
            <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
              {formatTime(elapsedTime)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<PlayArrowIcon />}
              onClick={runCode}
              disabled={isLoading || !code.trim()}
            >
              Run Code
            </Button>
            <Button
              variant="outlined"
              onClick={runTests}
              disabled={isLoading || !code.trim()}
            >
              Run Tests
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={submitInterview}
              disabled={isLoading}
            >
              Submit Interview
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Floating Waveform */}
      <FloatingWaveform
        isAISpeaking={isAISpeaking}
        isUserSpeaking={isUserSpeaking}
        audioData={currentAudio}
      />
    </Box>
  );
};

export default MockInterviewSession;